import React from 'react';
import { loadPatientAvatar } from 'context/appContext';
import { useQuery } from '@tanstack/react-query';
import Icon from 'components/Icon/Icon';
import Image from '../Image/Image';

export type PatientProps = {
  avatar: string;
  avatarBgColor?: string;
  name: string;
  about: string;
  state: string;
  isLoadingPatient: boolean;
  onPatientClick: () => void;
  showArrowNext?: boolean;
};

const loadPatientAvatarQuery = (picture: string) => ({
  queryKey: ['patient-avatar', picture], // we want to refresh when picture string data changes
  queryFn: () => loadPatientAvatar(picture),
});

function Patient({
  avatar,
  avatarBgColor,
  name,
  about,
  state,
  onPatientClick,
  isLoadingPatient,
  showArrowNext,
}: PatientProps) {
  const { data } = useQuery({
    ...loadPatientAvatarQuery(avatar),
    enabled: !!avatar,
  });

  const signedAvatar = data || 'image_patient_avatar_placeholder.png';

  const modifyFullName = (fullname: string) => {
    const nameParts = fullname.split(' ');

    if (nameParts.length <= 2) {
      return fullname;
    }

    const firstName = nameParts[0].trim();

    const lastName = nameParts[nameParts.length - 1].trim();

    let middleInitials = '';

    if (nameParts.length > 2) {
      middleInitials = nameParts
        .slice(1, -1)
        .map(fndName => fndName[0])
        .join('')
        .trim();
    }

    return `${firstName} ${middleInitials}. ${lastName}`;
  };

  return (
    <div
      role="presentation"
      onClick={isLoadingPatient ? () => false : onPatientClick}
      className={`w-full flex justify-between laptop:grid laptop:grid-cols-5 gap-4 py-1 items-center 
      rounded-full hover:bg-[#F6FAFF] ${isLoadingPatient ? 'animate-pulse' : 'cursor-pointer'}`}
    >
      <div className="laptop:col-span-2 flex flex-row items-center space-x-6 w-full justify-end">
        <Image
          className="h-[48px] w-[48px] tablet:h-[45px] tablet:w-[45px] rounded-full shrink-0"
          src={signedAvatar}
          alt={name}
          bgColor={avatarBgColor}
        />
        <div className="w-full flex flex-col">
          <div
            title={name}
            className="font-semibold text-ellipsis whitespace-nowrap overflow-hidden"
          >
            {modifyFullName(name)}
          </div>
          <div className="flex justify-between w-full laptop:hidden items-baseline">
            <div className="text-sm inline-flex"> {about}</div>
            <div className="items-center flex">
              <button
                onClick={isLoadingPatient ? () => false : onPatientClick}
                type="button"
                className="bg-[#F2F4F5] text-xs rounded-full py-[2px] px-5 inline-flex items-center h-[35px] flex-shrink-0"
              >
                See more
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="items-center hidden laptop:flex">{about}</div>

      <div
        title={state}
        className="items-center hidden laptop:inline-block justify-start tablet:ml-[30%] text-ellipsis 
        whitespace-nowrap overflow-hidden"
      >
        {state}
      </div>

      <div className="items-center hidden laptop:flex justify-end">
        {!showArrowNext ? (
          <button
            onClick={isLoadingPatient ? () => false : onPatientClick}
            type="button"
            className="bg-gray-100 text-xs rounded-full py-[2px] px-5 inline-flex items-center 
            h-[35px] flex-shrink-0"
          >
            See more
          </button>
        ) : (
          <Icon id="iconArrowForward" className="w-4 group-hover:text-river-black mr-[15%]" />
        )}
      </div>
    </div>
  );
}

Patient.defaultProps = {
  avatarBgColor: '#cfdeb9',
};

export default Patient;
